import React, { useState, useRef, useEffect } from "react";
import Head from "next/head";
import Link from "next/link";
import Router from "next/router";
import "react-dates/initialize";
import { SingleDatePicker } from "react-dates";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { initializeStore } from "../store";
import axios from "axios";

// functions
import { slideUp, slideDown } from "../libs/slide";
import { getEventName, performance2ticket, performanceNotRequired } from "../libs/performance2ticket";
import { scrollToElementDiv } from "../libs/scrollToElement";
import { vivatimes2times } from "../libs/vivatimes2times";

// types
import { datesType, fullOrderType, ticketType, timeGroupsType, timeType } from "types";

// partials
import Layout from "../partial/Layout";

// components
import Container from "../components/Container";
import { Col, ColLeft, ColRight } from "../components/Col";
import OrderBox from "../components/OrderBox";
import HelpText from "../components/HelpText";
import DiscountBox from "../components/DiscountBox";
import TicketList from "../components/TicketList";
import Strip from "../components/UpsellCTA";
import Button from "../components/Button";
import Loading from "../components/Loading";
import {
  Panel,
  PanelSummary,
  PanelBody,
  PanelSummaryRight,
  PanelSummaryLeft,
  PanelSummaryTitle,
  PanelSummaryDetails,
} from "../components/Panel";

import { findAllProductByStatGroup } from "api/queries/findAllProductByStatGroup";
import { findAllStatisticalGroup } from "api/queries/findAllStatisticalGroup";
import Upsell from "components/Upsell";
import alphanumeric from "libs/makeIDs";
import groupTimes from "libs/groupTimes";
import AvailableTimes from "components/AvailableTimes";
import PrevLink from "components/PrevLink";
import EditLink from "components/EditLink";
import timeSelectValidation from "libs/timeSelectValidation";
import easyDateCreator from "libs/easyDateCreator";
import createRangeDates from "libs/createRangeDates";
import { measureCheckout } from "libs/gtm";

const useReduxState = () => {
  return useSelector((state: fullOrderType) => ({
    event: state.event,
    avaTimes: state.avaTimes,
    avaTimeGroups: state.avaTimeGroups,
    totalFee: state.totalFee,
    totalTickets: state.totalTickets,
    upsells: state.upsells,
    membershipOffered: state.membershipOffer,
    donation: state.donation,
    donationAk: state.donationAk,
    coupon: state.discountCode,
    eventLists: state.eventLists,
  }));
};

const useUpdates = () => {
  //const discount = useSelector((state) => state.discount)
  const dispatch = useDispatch();

  const updateEventID = (eventID) => {
    dispatch({ type: "UPDATE_EVENTID", eventID });
  };

  const updateEventName = (eventName) => {
    dispatch({ type: "UPDATE_EVENTNAME", eventName });
  };

  const addMembership = () => {
    dispatch({ type: "ADD_MEMBERSHIP" });
  };

  const importTickets = (tickets) => {
    dispatch({ type: "IMPORT_TICKETS", tickets });
  };

  const updateTime = (time) => {
    dispatch({ type: "UPDATE_TIME", time });
  };

  const updateDates = (dates: string[]) => {
    dispatch({ type: "UPDATE_DATES", dates });
  };

  const updateDate = (date) => {
    dispatch({ type: "UPDATE_DATE", date });
  };

  const updateStepOne = (val) => {
    dispatch({ type: "UPDATE_STEPONE", val });
  };

  const updatePerf = (val) => {
    dispatch({ type: "UPDATE_PERFORMANCE", val });
  };

  const updateStepTwo = (val) => {
    dispatch({ type: "UPDATE_STEPTWO", val });
  };

  const updateStepThree = (val) => {
    dispatch({ type: "UPDATE_STEPTHREE", val });
  };

  const updateAvailableTimes = (val) => {
    dispatch({ type: "UPDATE_AVATIMES", val });
  };

  const updateAvailableTimeGroups = (val) => {
    dispatch({ type: "UPDATE_AVATIMEGROUPS", val });
  };

  const updateUpsells = (val) => {
    dispatch({ type: "UPDATE_UPSELLS", val });
  };

  const updateFullDayEvent = (perfID) => {
    dispatch({ type: "UPDATE_FULLTIMEPERF", perfID });
  };

  const updateTotal = (val) => {
    dispatch({ type: "UPDATE_TOTAL", val });
  };

  const updateDiscount = (val) => {
    dispatch({ type: "UPDATE_DISCOUNT", val });
  };

  return {
    updateDiscount,
    updateFullDayEvent,
    updateAvailableTimes,
    updateAvailableTimeGroups,
    updateEventID,
    updateEventName,
    importTickets,
    addMembership,
    updateTime,
    updateDate,
    updateStepOne,
    updateStepTwo,
    updateStepThree,
    updatePerf,
    updateUpsells,
    updateTotal,
    updateDates,
  };
};

const Index = (props) => {
  //console.log(props);

  const { event, membershipOffered, totalTickets, avaTimes, avaTimeGroups, donationAk, donation, coupon, eventLists } =
    useReduxState();

  const {
    updateUpsells,
    updateAvailableTimes,
    updateAvailableTimeGroups,
    updateEventID,
    updateEventName,
    addMembership,
    updateTime,
    updateDate,
    updateStepOne,
    updateStepTwo,
    updateStepThree,
    importTickets,
    updatePerf,
    updateFullDayEvent,
    updateTotal,
    updateDiscount,
    updateDates,
  } = useUpdates();

  const webPerformances = performance2ticket(props.allProductByStatGroup.PRODUCTLIST.PRODUCT);
  const eventName = getEventName(props.allProductByStatGroup.PRODUCTLIST.PRODUCT);

  const [, setPerfSelected] = useState(event.perfID);
  const [membershipSale, setMembershipSale] = useState(false);
  const [daySelected, setDaySelected] = useState<moment.Moment | null>(event.date);
  const [timeSelected, setTimeSelected] = useState(event.time);
  const [availableTimes, setAvailableTimes] = useState<timeType[]>(avaTimes);
  const [easyDatesDays, setEasyDatesDays] = useState<datesType[]>([]);
  const [blockedDates, setBlockedDates] = useState<any[]>([]);
  const [eventMains, setEventMains] = useState<string[]>(eventLists);
  const [dateError, setDateError] = useState("");

  const [availableTimeGroups, setAvailableTimeGroups] = useState<timeGroupsType[]>(avaTimeGroups);

  const [membershipOfffer, setMembershipOfffer] = useState(!membershipOffered);
  const [focused, setFocused] = useState(false);
  const [stepLoading, setStepLoading] = useState("");
  const [noAvatime, setNoAvatime] = useState("");

  const [stepOne, setStepOne] = useState<"on" | "off" | "">(event.stepOne);
  const [stepTwo, setStepTwo] = useState<"on" | "off" | "">(event.stepTwo);
  const [stepThree, setStepThree] = useState<"on" | "off" | "">(event.stepThree);

  const firstStep = useRef() as React.MutableRefObject<HTMLDivElement>;
  const secondStep = useRef() as React.MutableRefObject<HTMLDivElement>;
  const thirdStep = useRef() as React.MutableRefObject<HTMLDivElement>;

  const getTickets = () => {
    let selectedTickets = [] as any;
    event.tickets.forEach((ticket) => {
      if (ticket.num > 0) {
        selectedTickets.push({ id: ticket.id, num: ticket.num });
      }
    });
    return JSON.stringify(selectedTickets);
  };

  const getTicketsBasket = () => {
    let selectedTickets = [] as any;
    event.tickets.forEach((ticket) => {
      if (ticket.num > 0) {
        selectedTickets.push({ id: ticket.id, num: ticket.num });
      }
    });
    if (donationAk != "" && donation > 0) {
      selectedTickets.push({ id: donationAk, num: 1 });
    }
    return JSON.stringify(selectedTickets);
  };

  const updateBasket = async () => {
    const basketdata = await axios.get(`/api/checkbasket?productid=${getTicketsBasket()}&coupon=${coupon}`);
    if (basketdata.data.response.BASKETTOTAL && basketdata.data.response.BASKETTOTAL.GROSS) {
      updateTotal((parseFloat(basketdata.data.response.BASKETTOTAL.GROSS._text) || 0).toFixed(2));
    } else {
      updateTotal((0).toFixed(2));
    }
    if (
      basketdata.data.response.PROMOTIONAPPLIEDLIST &&
      basketdata.data.response.PROMOTIONAPPLIEDLIST.PROMOTIONAPPLIED
    ) {
      updateDiscount(
        (parseFloat(basketdata.data.response.PROMOTIONAPPLIEDLIST.PROMOTIONAPPLIED.DISCOUNT._text) * -1 || 0).toFixed(2)
      );
    } else {
      updateDiscount((0).toFixed(2));
    }
  };

  const goTo = async (step: number) => {
    setNoAvatime("");
    const selectedTicketList = event.tickets
      .filter((ticket) => ticket.num > 0)
      .map(({ ak }) => {
        const akMainID = ak.split(".");
        return `${akMainID[0]}.${akMainID[1]}`;
      });
    //  back steps
    if (step === 1 && (stepTwo == "on" || stepThree == "on")) {
      // go back to step 1
      setStepOne("on");
      updateStepOne("on");
      if (firstStep.current && secondStep.current && thirdStep.current) {
        scrollToElementDiv(firstStep.current, 250);
        slideUp(secondStep.current);
        slideUp(thirdStep.current);
        slideDown(firstStep.current);
      }
      setTimeout(() => {
        setStepTwo("off");
        updateStepTwo("off");
        setStepThree("off");
        updateStepThree("off");
      }, 400);
    }
    if (step === 2 && stepThree == "on") {
      // step 3 to step 2
      setStepTwo("on");
      updateStepTwo("on");
      if (secondStep.current && thirdStep.current) {
        scrollToElementDiv(firstStep.current, 25);
        slideUp(thirdStep.current);
        slideDown(secondStep.current);
      }
      setTimeout(() => {
        setStepThree("off");
        updateStepThree("off");
      }, 400);
    }
    // forward steps
    if (step === 2 && stepOne == "on") {
      // step 1 to step 2
      setStepOne("");
      updateStepOne("");
      setStepTwo("on");
      updateStepTwo("on");
      if (firstStep.current && secondStep.current) {
        scrollToElementDiv(firstStep.current, 0);
        slideUp(firstStep.current);
        slideDown(secondStep.current);
      }
    }
    if (step === 3 && (stepTwo == "on" || membershipSale)) {
      // step 2 to step 3

      if (availableTimeGroups.length > 0) {
        const timeSelectResult = timeSelectValidation(availableTimeGroups);
        setAvailableTimeGroups(timeSelectResult.times);
        if (!timeSelectResult.status) {
          return;
        }
      }

      const { data } = await axios.get(`/api/findupsell?productid=${getTickets()}`);
      updateUpsells(data.upsells);
      if (data.status === false) {
        Router.push(`/details/${props.urlCode}`, `/details/${props.urlCode}`, {
          shallow: true,
        }).then(() => window.scrollTo(0, 0));
        // console.log("selectedTicketList", selectedTicketList);
        // console.log("eventMains", eventMains);
        // if (
        //   selectedTicketList.length === eventMains.length ||
        //   selectedTicketList.every((value, index) => value === eventMains[index]) ||
        //   membershipSale
        // ) {
        //   Router.push(`/details/${props.urlCode}`, `/details/${props.urlCode}`, {
        //     shallow: true,
        //   }).then(() => window.scrollTo(0, 0));
        // } else {
        //   setDateError("Please select a date.");
        //   return;
        // }
      } else if (secondStep.current && thirdStep.current) {
        setStepTwo("");
        updateStepTwo("");
        setStepThree("on");
        updateStepThree("on");
        scrollToElementDiv(secondStep.current, 0);
        slideUp(secondStep.current);
        slideDown(thirdStep.current);
        updateBasket();
      }
    }
    if (step === 4 && (stepTwo == "on" || stepThree == "on")) {
      if (
        selectedTicketList.length === eventMains.length &&
        selectedTicketList.every((value, index) => value === eventMains[index])
      ) {
        Router.push(`/details/${props.urlCode}`, `/details/${props.urlCode}`, {
          shallow: true,
        }).then(() => window.scrollTo(0, 0));
      } else {
        setDateError("Please select a date.");
        return;
      }
    }
  };

  const Upgraded = () => {
    addMembership();
    setMembershipOfffer(false);
    goTo(1);
  };

  const selectPerf = (perfID: string) => {
    // selectPerf(availableTime.perfID);perfSelected
    setPerfSelected(perfID);
    updatePerf(perfID);
  };

  const selectTime = (time: string) => {
    updateTime(time);
    setTimeSelected(time);
  };

  const selectPerform = (perfID: string, selected: "on" | "") => {
    let availableTimeGroupsTemp = [...availableTimeGroups];
    const [bizIDCode, eventIDCode] = perfID.split(".");
    const eventCode = `${bizIDCode}.${eventIDCode}`;
    const index = availableTimeGroupsTemp.findIndex((obj) => obj.code == eventCode);
    if (selected === "on") {
      for (let x = 0; x < availableTimeGroupsTemp[index].times.length; x++) {
        availableTimeGroupsTemp[index].times[x].selected = "";
      }
    } else {
      const perfIndex = availableTimeGroupsTemp[index].times.findIndex((obj) => obj.perfID == perfID);
      for (let x = 0; x < availableTimeGroupsTemp[index].times.length; x++) {
        availableTimeGroupsTemp[index].times[x].selected = x === perfIndex ? "on" : "";
      }
    }
    const timeSelectResult = timeSelectValidation(availableTimeGroupsTemp);
    setAvailableTimeGroups(timeSelectResult.times);
  };

  const selectDate = async (date) => {
    const selectedTicketList = event.tickets
      .filter((ticket) => ticket.num > 0)
      .map(({ ak }) => ak)
      .join();

    setNoAvatime("");
    updateFullDayEvent("");
    setDaySelected(null);
    setStepLoading("time");
    const { data } = await axios.get(
      //`/api/gettimes?qdate=${moment(date).format("YYYY-MM-D")}&performanceGroup=${props.urlCode}`
      `/api/gettimes?qdate=${moment(date).format("YYYY-MM-D")}&products=${selectedTicketList}`
    );

    // console.log(data)

    try {
      let times = vivatimes2times(data.response);
      setEventMains(data.performanceMains);
      setAvailableTimes(times);
      updateAvailableTimes(times);
      if (times.length < 2) {
        setAvailableTimeGroups([]);
        updateAvailableTimeGroups([]);
        selectTime(times[0].time);
        selectPerf(times[0].perfID);
      } else {
        const avagrps = await groupTimes(times);
        setAvailableTimeGroups(avagrps);
        updateAvailableTimeGroups(avagrps);
      }

      setTimeout((_) => {
        setStepLoading("");
        updateDate(date);
        setDaySelected(date);
      }, 600);
    } catch (error) {
      setStepLoading("");
      updateDate(date);
      setDaySelected(date);
      selectTime("");
      selectPerf("");
      setNoAvatime("There is no available times for this date.");
    }
  };

  const isDayBlocked = (day) => {
    return blockedDates.filter((d) => d.isSame(day, "day")).length === 0;
  };

  useEffect(() => {
    if (event.id === "IDHERE") {
      importTickets(webPerformances);
      updateEventName(eventName);
    }
    updateEventID(props.urlCode);

    const { fromDate, toDate } = createRangeDates();
    axios.get(`/api/getdays?fromdate=${fromDate}&todate=${toDate}&performanceGroup=${props.urlCode}`).then(
      (res) => {
        try {
          let days: any[] = [];
          let avaliableDays: any[] = [];
          if (Array.isArray(res.data.response.DAYLIST.DAY)) {
            days = [...easyDateCreator(res.data.response.DAYLIST.DAY)];
          } else {
            days.push({
              text: moment(res.data.response.DAYLIST.DAY.DATE._text).format("D MMM"),
              moment: moment(res.data.response.DAYLIST.DAY.DATE._text),
            });
          }

          days.map((day) => avaliableDays.push(day.moment));

          setBlockedDates(avaliableDays);
          setEasyDatesDays(days);
          updateDates(days);
        } catch (error) {
          console.error(error);
          setEasyDatesDays([]);
        }
      },
      (error) => {
        console.error(error);
      }
    );
  }, []);

  useEffect(() => {
    // console.log(props.allProductByStatGroup.PRODUCTLIST.PRODUCT)
    const dateNotRequired = performanceNotRequired(props.allProductByStatGroup.PRODUCTLIST.PRODUCT);

    setMembershipSale(dateNotRequired);
  }, [props]);

  // GTM Measure checkout steps
  useEffect(() => {
    const products: GTMProduct[] = event.tickets.reduce(
      (prevVal: GTMProduct[], ticket: ticketType) => {
        if (ticket.num > 0) {
          return [
            ...prevVal,
            {
              id: ticket.id,
              name: ticket.name,
              category: ticket.category,
              price: ticket.price,
              quantity: ticket.num,
            },
          ];
        }
        return prevVal;
      },
      [],
    );

    if (products.length > 0) {
      if (stepTwo) {
        measureCheckout({
          step: 1,
          option: 'How many people are coming?',
          products,
        });
      } else if (stepThree) {
        measureCheckout({
          step: 2,
          option: 'What day would you like to come?',
          products,
        });
      }
    }
  }, [stepTwo, stepThree]);

  return (
    <>
      <Head>
        <title>Order your ticket</title>
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Layout step={1} urlCode={props.urlCode}>
        <Container>
          <Col>
            <ColLeft>
              <Panel>
                <PanelSummary>
                  <PanelSummaryLeft>
                    <PanelSummaryTitle>{!membershipSale && `Ticket `}Selection</PanelSummaryTitle>
                    <PanelSummaryDetails>{event.name}</PanelSummaryDetails>
                  </PanelSummaryLeft>
                  <PanelSummaryRight>
                    {/*<a href={backToTicketsLink} className="panel__summary-link">
                      BACK TO TICKETS
                    </a>*/}
                  </PanelSummaryRight>
                </PanelSummary>
              </Panel>

              <Panel active={stepOne}>
                <PanelSummary>
                  <PanelSummaryLeft>
                    <PanelSummaryTitle>How many people are coming?</PanelSummaryTitle>
                    <PanelSummaryDetails>
                      {event.tickets.map((ticket, i) =>
                        ticket.num > 0 ? (
                          <span className="panel__summary-list" key={i}>
                            {ticket.num} x {ticket.type}
                          </span>
                        ) : (
                          ""
                        )
                      )}
                    </PanelSummaryDetails>
                  </PanelSummaryLeft>
                  <PanelSummaryRight>
                    <EditLink
                      id="edit-1"
                      onClick={() => {
                        goTo(1);
                      }}
                    />
                  </PanelSummaryRight>
                </PanelSummary>

                <PanelBody>
                  {stepOne == "on" ? (
                    <>
                      {membershipSale ? (
                        <div style={{ height: "20px" }}></div>
                      ) : (
                        <PanelSummary>
                          <PanelSummaryLeft>
                            <PanelSummaryTitle>How many people are coming?</PanelSummaryTitle>
                            <PanelSummaryDetails>
                              Please select quantity below. You will receive your ticket via email.
                            </PanelSummaryDetails>
                          </PanelSummaryLeft>
                        </PanelSummary>
                      )}
                    </>
                  ) : (
                    ""
                  )}
                  <div className="slide" ref={firstStep}>
                    <TicketList event={event} />
                    <div className="actions actions--margint10v">
                      <Link href="https://www.sea.museum/">
                        <a className="actions__prev">CANCEL ORDER</a>
                      </Link>
                      <Button
                        btnClick={(e) => {
                          e.preventDefault();
                          goTo(membershipSale ? 3 : 2);
                        }}
                        btnType="button"
                        btnClass="yellow,large,bold"
                        disabled={totalTickets < 1}
                        id={`continue-to-2`}
                      >
                        CONTINUE
                      </Button>
                    </div>
                  </div>
                </PanelBody>
              </Panel>

              <Panel active={stepTwo}>
                <PanelSummary>
                  <PanelSummaryLeft>
                    <PanelSummaryTitle>What day would you like to come?</PanelSummaryTitle>
                    <PanelSummaryDetails>
                      {daySelected ? daySelected.format("dddd D MMMM YYYY") : ""}
                    </PanelSummaryDetails>
                  </PanelSummaryLeft>
                  <PanelSummaryRight>
                    <EditLink
                      id="edit-2"
                      onClick={() => {
                        goTo(2);
                      }}
                    />
                  </PanelSummaryRight>
                </PanelSummary>
                <PanelBody>
                  <div className="slide" ref={secondStep}>
                    {false && membershipOfffer && (
                      <Strip
                        title="Why not become a member today?"
                        body="Enjoy the benefits of a member pass all year long!"
                      >
                        <Button id={`upgrade-now`} btnType="button" btnClass="white,large,bold" btnClick={Upgraded}>
                          UPGRADE NOW
                        </Button>
                      </Strip>
                    )}
                    <PanelSummary>
                      <PanelSummaryLeft>
                        <PanelSummaryTitle>
                          What day would you like to come? {stepLoading == "time" && <Loading />}
                        </PanelSummaryTitle>
                        <PanelSummaryDetails>
                          Please select a day {availableTimes.length > 1 && ` and time`} to visit
                        </PanelSummaryDetails>
                      </PanelSummaryLeft>
                    </PanelSummary>
                    <div className="pick-days">
                      {easyDatesDays.slice(0, 4).map((easyDate, i) => {
                        return (
                          <button
                            key={i}
                            onClick={(e) => {
                              e.preventDefault();
                              selectDate(easyDate.moment);
                            }}
                            type="button"
                            className={`select-btn ${daySelected === easyDate.moment ? "is-active" : ""}`}
                            id={`select-day-${alphanumeric(easyDate.text)}`}
                          >
                            {easyDate.text}
                          </button>
                        );
                      })}
                    </div>

                    <div className="date-picker">
                      <div className={`select-btn select-btn--date ${focused ? "is-active" : ""}`}>
                        <SingleDatePicker
                          //onClick={DatePickerActivate}
                          ariaLabel="FILTER BY DATE"
                          placeholder="FILTER BY DATE"
                          displayFormat={() => "dddd D MMMM YYYY"}
                          date={daySelected ? daySelected : null}
                          numberOfMonths={1}
                          showDefaultInputIcon={true}
                          small={true}
                          onDateChange={(d) => selectDate(d)}
                          focused={focused}
                          onFocusChange={() => setFocused(!focused)}
                          isDayBlocked={isDayBlocked}
                          id="datepicker"
                        />
                      </div>
                    </div>

                    {dateError && <p className="error">{dateError}</p>}
                    {noAvatime && <p>{noAvatime}</p>}

                    {availableTimes.length > 1 && (
                      <div className={`available-times ${noAvatime === "" && daySelected ? "is-active" : ""}`}>
                        <AvailableTimes times={availableTimeGroups} selectPerform={selectPerform} />
                      </div>
                    )}
                    <div className="actions actions--margint10v">
                      <PrevLink
                        id="goback-to-1"
                        onClick={() => {
                          goTo(1);
                        }}
                      />
                      <Button
                        btnClick={(e) => {
                          e.preventDefault();
                          goTo(3);
                        }}
                        btnType="button"
                        btnClass="yellow,large,bold"
                        disabled={availableTimes.length > 1 ? !daySelected : !daySelected || timeSelected === ""}
                        id={`continue-to-3`}
                      >
                        CONTINUE
                      </Button>
                    </div>
                  </div>
                </PanelBody>
              </Panel>

              <Panel active={stepThree}>
                <PanelBody>
                  {stepThree == "on" ? (
                    <PanelSummary>
                      <PanelSummaryLeft>
                        <PanelSummaryTitle>Add an event or exhibition to your day?</PanelSummaryTitle>
                        <PanelSummaryDetails>
                          <div className="panel-red-text">
                            <b>Note:</b> Adding an event or exhibition will apply to all previously selected tickets
                          </div>
                        </PanelSummaryDetails>
                      </PanelSummaryLeft>
                    </PanelSummary>
                  ) : (
                    ""
                  )}
                  <div className="slide" ref={thirdStep}>
                    <div className="add-offers">
                      <Upsell />
                    </div>

                    <div className="actions actions--margint10v">
                      <PrevLink
                        id="goback-to-2"
                        onClick={() => {
                          goTo(2);
                        }}
                      />
                      <Button
                        btnClick={(e) => {
                          e.preventDefault();
                          goTo(4);
                        }}
                        btnType="button"
                        btnClass="yellow,large,bold"
                        disabled={event.totalTickets < 1}
                        id={`continue-to-4`}
                      >
                        CONTINUE
                      </Button>
                    </div>
                  </div>
                </PanelBody>
              </Panel>
            </ColLeft>
            <ColRight>
              <OrderBox />
              <DiscountBox />
              <HelpText />
            </ColRight>
          </Col>
        </Container>
      </Layout>
    </>
  );
};

export default Index;

export async function getInitialProps() {
  const reduxStore = initializeStore();
  return { props: { initialReduxState: reduxStore.getState() } };
}

export async function getServerSideProps(context) {
  let code = context.query.event;
  if (!code) {
    Router.push(`/505`, `/505`, {
      shallow: true,
    }).then(() => window.scrollTo(0, 0));
    return;
  }
  let allProductByStatGroup;

  const categoryList: string[] = [];
  const categories = await findAllStatisticalGroup();
  categories.map((category) => categoryList.push(category.CODE._text));

  try {
    allProductByStatGroup = await findAllProductByStatGroup(code);

    if (!categoryList.includes(code)) {
      context.res.statusCode = 302;
      context.res.setHeader("Location", "/404");
      context.res.end();
      return {};
    }

    if (!allProductByStatGroup.PRODUCTLIST) {
      context.res.statusCode = 302;
      context.res.setHeader("Location", "/expired");
      context.res.end();
      return {};
    }
  } catch (error) {
    context.res.statusCode = 302;
    context.res.setHeader("Location", "/505");
    context.res.end();
    return {};
  }
  return { props: { allProductByStatGroup, urlCode: code } };
}
